// dqAPI.ts
// =========
// Cross-entity / cross-table DQ rules API.
//
// ┌─────────────────────────────────────────────────────────────────────────┐
// │  NAMING CONVENTION — read before importing                              │
// │                                                                         │
// │  API.ts    getDQSummary()     → DQLifecycleSummary                      │
// │            Endpoint: GET /adriva_api/dq/lazy/dq-summary/{entity_id}     │
// │            Shape: { success, total, by_lifecycle, by_category, … }      │
// │            Used by: DMTreeGridSummary lifecycle pill counts              │
// │                                                                         │
// │  dqApi.ts  getCrossDQSummary() → DQCrossEntitySummary                   │
// │            Endpoint: GET /dq/{entity_id}/summary                        │
// │            Shape: { total_rules, pass, fail, cross_table_rules, … }     │
// │            Used by: DQRulesReport, cross-entity modal badge             │
// └─────────────────────────────────────────────────────────────────────────┘

import { api as apiClient } from "./API";

// ─── Shared primitive types ───────────────────────────────────────────────────

export type RuleStatus  = "PASS" | "FAIL" | "ERROR" | "NO_OP" | "APPLIED" | "NOT_RUN";
export type Severity    = "H" | "M" | "L";
export type Category    = "val" | "cons" | "std";
export type RuleAction  = "Reject" | "Warn" | "AutoFix" | "Manual";
export type RuleLifecycleFilter = "All" | "Approved" | "Draft" | "Rejected" | "Waiting";
export type RuleExportFormat = "xlsx" | "html" | "csv" | "json" | "sql";
export type SqlDialect       = "tsql" | "postgres" | "oracle";

const _EXPORT_MIME: Record<RuleExportFormat, string> = {
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  html: "text/html",
  csv:  "text/csv",
  json: "application/json",
  sql:  "application/sql",
};

export interface SampleFailure {
  index:           number;
  value_checked:   Record<string, unknown> | null;
  failure_reason:  string;
  error?:          string;
}

export interface VeExpression {
  fn:   string;
  args: unknown[];
}

export interface ExamplePair {
  before: string;
  after:  string;
  note:   string;
}

// ─── CrossTableRule ───────────────────────────────────────────────────────────
// One rule that references columns from more than one source_prefix.
// Returned inside DQCrossEntitySummary and as items in getCrossTableRules().

export interface CrossTableRule {
  id:              string;
  d:               string;          // short description ≤15 words
  bi:              string;          // business impact
  fix?:            string;          // recommended remediation
  vl?:             string;          // human-readable logic
  ve?:             VeExpression;    // DSL expression
  sev:             Severity;
  act:             RuleAction;
  cat:             Category;
  score:           number;          // 1–10
  rule_type:       "business" | "technical";
  lifecycle:       string;          // "Draft" | "Approved" | "Rejected" | …
  fn:              string | null;   // ve.fn shorthand, e.g. "column_subset_of"
  prefixes:        string[];        // e.g. ["EQUIPMENTS", "WORK_ORDER"]
  anchor_col:      string;
  anchor_prefix:   string;
  fields:          string[];
  exv?:            string[];
  ex?:             ExamplePair[];
  n?:              string;          // analyst note
  // ── execution results (NOT_RUN when rule has never been executed) ──────
  status:          RuleStatus;
  fail_count:      number;
  passed_count:    number;
  total_records:   number;
  sample_failures: SampleFailure[];
}

// ─── DQCrossEntitySummary ─────────────────────────────────────────────────────
// Returned by GET /dq/{entity_id}/summary
// Full cross-entity health snapshot: execution stats + all cross-table rules.
//
// DO NOT confuse with DQLifecycleSummary (API.ts) which only contains
// lifecycle pill counts (total, by_lifecycle, by_category, by_dimension)
// and is served by GET /adriva_api/dq/lazy/dq-summary/{entity_id}.

export interface DQCrossEntitySummary {
  entity_id:               string;
  generated_at:            string;
  total_rules:             number;
  rules_executed:          number;
  pass:                    number;
  fail:                    number;
  error:                   number;
  no_op:                   number;
  prefixes:                string[];
  sev_counts:              Record<string, number>;
  cat_counts:              Record<string, number>;
  cross_table_rule_count:  number;
  cross_table_rules:       CrossTableRule[];
}

// ─── DQRuleDetail ─────────────────────────────────────────────────────────────
// Returned by GET /dq/{entity_id}/rule/{rule_id}

export interface SiblingRule {
  id:             string;
  d:              string;
  sev:            Severity;
  cat:            Category;
  fn:             string | null;
  status:         RuleStatus;
  fail_count:     number;
  total_records:  number;
}

export interface ColumnStats {
  total_rules_on_col: number;
  fail_count_on_col:  number;
  pass_count_on_col:  number;
}

export interface DQRuleDetail {
  rule:          CrossTableRule;
  sibling_rules: SiblingRule[];
  column_stats:  ColumnStats;
}

// ─── API functions ────────────────────────────────────────────────────────────

const BASE = "/adriva_api/dq";

// =============================================================================
// APPEND to dqAPI.ts
// =============================================================================
// Single-field rule generation / tuning for the Rule Studio UI.
// Synchronous: resolves with the generated rules (+ optional PASS/FAIL preview).
// Nothing is persisted here — the UI accepts rules via the existing
// adriva_dq_postAction(DQ_RULE_ACCEPT) flow.
//
// Uses the shared apiClient (imported at the top of dqAPI.ts as `api`).
// =============================================================================

// ── Request / response types ──────────────────────────────────────────────────

export interface RuleGenerateRequest {
  entity_id:        string;
  app_nm:           string;
  source_prefix:    string;
  column:           string;                 // orig_column_name
  category?:        Category | null;        // "val" | "cons" | "std" | null
  user_directive?:  string | null;          // free-text English instruction
  existing_rule?:   Record<string, unknown> | null;  // present => tune
  execute_preview?: boolean;                // default true
}

// A generated rule mirrors the stored rule shape (id, cat, field, ve/te, …).
// Kept loose so the UI can render whatever the agent returns.
export interface GeneratedRule {
  id:         string;
  cat:        Category;
  field:      string | string[];
  d?:         string;
  vl?:        string | null;
  ve?:        VeExpression | null;
  vc?:        string | null;
  td?:        string | null;
  te?:        VeExpression | null;
  sev?:       Severity;
  act?:       RuleAction;
  fix?:       string;
  bi?:        string;
  exv?:       string[];
  ex?:        ExamplePair[];
  conf?:      Severity;
  dq_dim?:    string;
  score?:     number;
  rule_type?: "business" | "technical";
  lifecycle?: string;
  n?:         string | null;
  [k: string]: unknown;
}

export interface RuleValidationResult {
  rule_id:          string;
  status:           RuleStatus | string;
  fail_count?:      number;
  passed_count?:    number;
  total_records?:   number;
  sample_failures?: SampleFailure[];
  examples?:        Record<string, unknown>;
  dimension?:       string;
  [k: string]: unknown;
}

export interface RuleGenerateResponse {
  success:             boolean;
  mode:                string | null;
  requested_category:  Category | null;
  source_prefix:       string;
  column:              string;
  rules:               GeneratedRule[];
  validation_results:  RuleValidationResult[];
  error?:              string | null;
}

// ── API call ───────────────────────────────────────────────────────────────────

/**
 * Generate new rules (Add Rule) or refine an existing rule (Tune Rule) for ONE
 * field. Synchronous — resolves with the rules and an optional PASS/FAIL preview.
 *
 * Add Rule:  omit `existing_rule`. Optionally set `category` and/or `user_directive`.
 * Tune Rule: pass the rule object in `existing_rule` (its `id` is preserved).
 *
 * Endpoint: POST /adriva_api/dq/rule/generate
 */
export async function adriva_dq_generateRule(
  req: RuleGenerateRequest,
): Promise<RuleGenerateResponse> {
  const res = await apiClient.post<RuleGenerateResponse>(
    `${BASE}/rule/generate`,
    {
      entity_id:       req.entity_id,
      app_nm:          req.app_nm,
      source_prefix:   req.source_prefix,
      column:          req.column,
      category:        req.category ?? null,
      user_directive:  req.user_directive ?? null,
      existing_rule:   req.existing_rule ?? null,
      execute_preview: req.execute_preview ?? true,
    },
    { timeout: 5 * 60 * 1000 },   // LLM round-trip + execution can take a while
  );
  return res.data;
}

/**
 * Cross-entity health stats + full cross-table rule list.
 * Powers DQRulesReport and the cross-entity modal badge.
 * Endpoint: GET /dq/{entity_id}/summary
 */
export async function getCrossDQSummary(
  entityId: string
): Promise<DQCrossEntitySummary> {
  const res = await apiClient.get<DQCrossEntitySummary>(
    `${BASE}/${entityId}/summary`
  );
  return res.data;
}

/**
 * All cross-table rules with live execution results.
 * Endpoint: GET /dq/{entity_id}/rules/cross-table
 */
export async function getCrossTableRules(
  entityId: string
): Promise<CrossTableRule[]> {
  const res = await apiClient.get<{ entity_id: string; rules: CrossTableRule[] }>(
    `${BASE}/${entityId}/rules/cross-table`
  );
  return res.data.rules;
}

/**
 * Full detail for one rule + sibling rules on the same anchor column.
 * Endpoint: GET /dq/{entity_id}/rule/{rule_id}
 */
export async function getDQRuleDetail(
  entityId: string,
  ruleId:   string
): Promise<DQRuleDetail> {
  const res = await apiClient.get<DQRuleDetail>(
    `${BASE}/${entityId}/rule/${encodeURIComponent(ruleId)}`
  );
  return res.data;
}


export async function downloadDQRulesExport(
  entityId:  string,
  appNm:     string,
  lifecycle: RuleLifecycleFilter = "All",
  format:    RuleExportFormat = "xlsx",
  dialect:   SqlDialect = "tsql",
  transactional: boolean = true,
): Promise<void> {
  const res = await apiClient.post(
    `${BASE}/export-dq-rules`,
    { entity_id: entityId, app_nm: appNm, lifecycle, format, dialect, transactional },
    { responseType: "blob" },
  );
 
  // sql/json filenames carry the dialect to disambiguate multi-DB downloads
  const ext  = format;
  const dpart = (format === "sql") ? `_${dialect}` : "";
  const name = `DQ_Rules_${entityId}_${lifecycle}${dpart}.${ext}`;
 
  const blob = new Blob([res.data], { type: _EXPORT_MIME[format] || "application/octet-stream" });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}